// Phase MHM-5.4 · Micro Hub Workers page.
import { useEffect, useState, useCallback } from "react";
import { useSearchParams } from "react-router-dom";
import { Loader2, RefreshCw, Plus, X, ToggleLeft, ToggleRight, Trash2, Pencil } from "lucide-react";
import { toast } from "sonner";
import api from "../../services/api";
import RoleGate from "../../components/RoleGate";
import MicroLeadSubNav from "../../components/microlead/MicroLeadSubNav";

const ROLES = ["packer", "loader", "qc", "general"];

function WorkerModal({ open, onClose, onSaved, initial }) {
  // Dual-purpose modal: create when initial is null, edit otherwise.
  const isEdit = !!initial;
  const [name, setName] = useState(""); const [phone, setPhone] = useState("");
  const [role, setRole] = useState("packer"); const [notes, setNotes] = useState("");
  const [grantLogin, setGrantLogin] = useState(false);
  const [email, setEmail] = useState("");
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (!open) return;
    setName(initial?.name || "");
    setPhone(initial?.phone || "");
    setRole(initial?.staff_role || "packer");
    setNotes(initial?.notes || "");
    setGrantLogin(false); setEmail("");
  }, [open, initial]);

  if (!open) return null;
  const submit = async () => {
    if (!name) return toast.error("Name is required");
    if (!isEdit && !phone) return toast.error("Phone is required");
    if (!isEdit && grantLogin && !email) return toast.error("Email required for login");
    setSaving(true);
    try {
      if (isEdit) {
        const body = { name, staff_role: role, notes: notes || null };
        if (phone) body.phone = phone;
        await api.patch(`/micro-hub/workers/${initial.worker_id}`, body);
        toast.success("Worker updated");
      } else {
        await api.post("/micro-hub/workers", {
          name, phone, staff_role: role, notes: notes || null,
          grant_login: grantLogin, email: email || null,
        });
        toast.success("Worker added");
      }
      onSaved(); onClose();
    } catch (e) { toast.error(e.userMessage || e?.response?.data?.detail || (isEdit ? "Update failed" : "Add failed")); }
    finally { setSaving(false); }
  };
  return (
    <div className="fixed inset-0 z-50 bg-black/40 flex items-center justify-center p-4"
         role="dialog" aria-modal="true" aria-labelledby="worker-modal-title"
         data-testid="microlead-worker-modal">
      <div className="bg-white rounded-2xl shadow-xl max-w-md w-full p-5">
        <div className="flex items-center justify-between mb-3">
          <h3 id="worker-modal-title" className="font-['Outfit'] text-lg font-semibold">
            {isEdit ? "Edit worker" : "Add worker"}
          </h3>
          <button onClick={onClose} aria-label="Close"><X className="h-4 w-4"/></button>
        </div>
        <div className="space-y-3 text-sm">
          <div>
            <label className="block text-xs text-slate-500 mb-1">Name *</label>
            <input data-testid="microlead-worker-name" value={name} onChange={(e) => setName(e.target.value)}
                   className="w-full border border-slate-200 rounded-lg px-2 py-1.5"/>
          </div>
          <div>
            <label className="block text-xs text-slate-500 mb-1">
              Phone {isEdit ? "(leave blank to keep)" : "*"}
            </label>
            <input data-testid="microlead-worker-phone" value={phone} onChange={(e) => setPhone(e.target.value)}
                   placeholder="+91" className="w-full border border-slate-200 rounded-lg px-2 py-1.5"/>
          </div>
          <div>
            <label className="block text-xs text-slate-500 mb-1">Staff role</label>
            <div className="flex flex-wrap gap-2">
              {ROLES.map((r) => (
                <label key={r} data-testid={`microlead-worker-role-${r}`}
                       className={`px-2 py-1 rounded-full text-xs border cursor-pointer ${role === r ? "bg-[#FF6B00] text-white border-[#FF6B00]" : "border-slate-200"}`}>
                  <input type="radio" name="role" value={r} checked={role === r}
                         onChange={() => setRole(r)} className="hidden"/>{r}
                </label>
              ))}
            </div>
          </div>
          <div>
            <label className="block text-xs text-slate-500 mb-1">Notes</label>
            <textarea data-testid="microlead-worker-notes" value={notes} onChange={(e) => setNotes(e.target.value)}
                      rows={2} className="w-full border border-slate-200 rounded-lg px-2 py-1.5"/>
          </div>
          {!isEdit && (
            <>
              <label className="flex items-center gap-2 text-sm">
                <input type="checkbox" checked={grantLogin} onChange={(e) => setGrantLogin(e.target.checked)}
                       data-testid="microlead-worker-grant-login" className="accent-[#FF6B00]"/>
                Grant login (creates packer_staff user)
              </label>
              {grantLogin && (
                <div>
                  <label className="block text-xs text-slate-500 mb-1">Login email</label>
                  <input data-testid="microlead-worker-email" value={email} onChange={(e) => setEmail(e.target.value)}
                         placeholder="ramesh@pickngo.com" className="w-full border border-slate-200 rounded-lg px-2 py-1.5"/>
                </div>
              )}
            </>
          )}
        </div>
        <div className="flex justify-end gap-2 mt-4">
          <button onClick={onClose} className="text-sm px-3 py-1.5 border border-slate-200 rounded-lg">Cancel</button>
          <button onClick={submit} disabled={saving}
                  data-testid="microlead-worker-submit"
                  className="text-sm px-3 py-1.5 bg-[#FF6B00] hover:bg-[#E55F00] text-white rounded-lg disabled:opacity-60">
            {saving ? (isEdit ? "Saving…" : "Adding…") : (isEdit ? "Save changes" : "Add worker")}
          </button>
        </div>
      </div>
    </div>
  );
}

function Inner() {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [modal, setModal] = useState(false);
  const [editing, setEditing] = useState(null);
  const [params] = useSearchParams();

  const load = useCallback(async () => {
    setLoading(true); setError(null);
    try {
      const { data } = await api.get("/micro-hub/workers?limit=200");
      setRows(data.rows || []);
    } catch (e) {
      setError(e.userMessage || "Failed to load workers");
    } finally { setLoading(false); }
  }, []);
  useEffect(() => { load(); }, [load]);

  // Auto-open the create modal if landed with ?new=1 (from dashboard "+ Add Worker")
  useEffect(() => { if (params.get("new") === "1") { setEditing(null); setModal(true); } }, [params]);

  const toggleActive = async (w) => {
    try {
      await api.patch(`/micro-hub/workers/${w.worker_id}`, { is_active: !w.is_active });
      toast.success(`${w.name} ${!w.is_active ? "activated" : "deactivated"}`); load();
    } catch (e) { toast.error(e.userMessage || "Update failed"); }
  };

  const del = async (w) => {
    if (!window.confirm(`Delete worker "${w.name}"? This cannot be undone.`)) return;
    try {
      await api.delete(`/micro-hub/workers/${w.worker_id}`);
      toast.success("Worker deleted"); load();
    } catch (e) { toast.error(e.userMessage || "Delete failed"); }
  };

  const openEdit = (w) => { setEditing(w); setModal(true); };
  const openCreate = () => { setEditing(null); setModal(true); };
  const closeModal = () => { setModal(false); setEditing(null); };

  return (
    <div className="max-w-[1400px] mx-auto px-4 sm:px-6 py-6 space-y-4" data-testid="microlead-workers-page">
      <div className="flex flex-wrap items-end justify-between gap-3">
        <div>
          <h1 className="text-2xl sm:text-3xl font-bold text-slate-900 font-['Outfit']" data-testid="microlead-workers-title">
            Workers
          </h1>
          <p className="text-sm text-slate-500">Packers, loaders, QC staff for this micro hub · {rows.length}</p>
        </div>
        <div className="flex gap-2">
          <button onClick={load} data-testid="microlead-workers-refresh"
                  className="inline-flex items-center gap-1.5 px-3 py-1.5 text-sm border border-slate-200 rounded-lg">
            {loading ? <Loader2 className="h-4 w-4 animate-spin"/> : <RefreshCw className="h-4 w-4"/>} Refresh
          </button>
          <button onClick={openCreate}
                  data-testid="microlead-workers-add"
                  className="inline-flex items-center gap-1.5 px-3 py-1.5 text-sm bg-[#FF6B00] hover:bg-[#E55F00] text-white rounded-lg">
            <Plus className="h-4 w-4"/> Add Worker
          </button>
        </div>
      </div>
      <div className="bg-white border border-slate-200 rounded-2xl overflow-hidden" data-testid="microlead-workers-table">
        <table className="w-full text-sm">
          <thead className="bg-slate-50 text-xs uppercase text-slate-500">
            <tr><th className="px-3 py-2 text-left">Name</th><th>Role</th><th>Phone</th>
                <th>Active</th><th>Has login</th><th>Notes</th><th>Actions</th></tr>
          </thead>
          <tbody className="divide-y divide-slate-100">
            {error && (
              <tr><td colSpan={7} className="text-center py-6 text-rose-600">{error}</td></tr>
            )}
            {!error && rows.length === 0 && !loading && (
              <tr><td colSpan={7} className="text-center py-10 text-slate-400">No workers yet</td></tr>
            )}
            {rows.map((w) => (
              <tr key={w.worker_id} data-testid={`microlead-worker-row-${w.worker_id}`}>
                <td className="px-3 py-2 font-medium">{w.name}</td>
                <td className="px-3 py-2 text-xs capitalize">{w.staff_role}</td>
                <td className="px-3 py-2 font-mono text-xs">{w.phone_masked}</td>
                <td className="px-3 py-2">
                  <button onClick={() => toggleActive(w)}
                          aria-label={`Toggle ${w.name} ${w.is_active ? "inactive" : "active"}`}
                          data-testid={`microlead-worker-toggle-${w.worker_id}`}
                          className="inline-flex items-center gap-1">
                    {w.is_active ? <ToggleRight className="h-5 w-5 text-green-600"/> : <ToggleLeft className="h-5 w-5 text-slate-400"/>}
                  </button>
                </td>
                <td className="px-3 py-2 text-xs">{w.has_login
                  ? <span className="bg-orange-50 text-[#C2410C] border border-orange-200 px-2 py-0.5 rounded-full">Yes</span>
                  : <span className="text-slate-400">—</span>}</td>
                <td className="px-3 py-2 text-xs text-slate-500 truncate max-w-[280px]">{w.notes || "—"}</td>
                <td className="px-3 py-2">
                  <div className="flex gap-2">
                    <button onClick={() => openEdit(w)}
                            data-testid={`microlead-worker-edit-${w.worker_id}`}
                            aria-label={`Edit ${w.name}`}
                            className="inline-flex items-center gap-1 text-xs text-[#C2410C] hover:text-[#FF6B00]">
                      <Pencil className="h-3.5 w-3.5"/> Edit
                    </button>
                    <button onClick={() => del(w)}
                            data-testid={`microlead-worker-delete-${w.worker_id}`}
                            aria-label={`Delete ${w.name}`}
                            className="inline-flex items-center gap-1 text-xs text-rose-600 hover:text-rose-800">
                      <Trash2 className="h-3.5 w-3.5"/> Delete
                    </button>
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      <WorkerModal open={modal} onClose={closeModal} onSaved={load} initial={editing}/>
    </div>
  );
}

export default function MicroHubWorkers() {
  return (
    <RoleGate allow={["micro_hub_lead", "main_hub_manager", "admin", "founder", "ceo"]}>
      <MicroLeadSubNav/>
      <Inner/>
    </RoleGate>
  );
}
